import { LayoutDashboard, Package, LogOut, AlertTriangle, TrendingDown, Users, Pencil, BookOpen, MessageCircle, FileText, RefreshCw, Building2, CreditCard, Calendar, BarChart3, Shield, MapPin, Settings, Truck, House, LineChart, UserRound } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { usePaywallModal } from "@/contexts/PaywallModalContext";
import { useIsPro } from "@/hooks/useIsPro";
import { useBusinessName } from "@/hooks/useBusinessName";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { useEmployeeViewSettings } from "@/hooks/useEmployeeViewSettings";
import { UserAvatar } from "@/components/UserAvatar";

export function AppSidebar() {
  const { role, signOut, user, businessId, isFounder, refreshProfile, profile, profileLoading } = useAuth();
  if (isFounder) return null;
  const { setOpenMobile } = useSidebar();
  const { settings: employeeViewSettings } = useEmployeeViewSettings();
  const isPro = useIsPro();
  const { openPaywall } = usePaywallModal();
  const location = useLocation();
  const closeMobileSidebar = () => setOpenMobile(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { businessName } = useBusinessName();
  const [hasCritical, setHasCritical] = useState(false);
  const [businessNameOpen, setBusinessNameOpen] = useState(false);
  const [businessNameDraft, setBusinessNameDraft] = useState("");
  const [savingBusinessName, setSavingBusinessName] = useState(false);

  useEffect(() => {
    if (!businessId) return;
    const checkCritical = async () => {
      const { data } = await supabase
        .from("inventory")
        .select("id, current_stock, min_threshold")
        .eq("business_id", businessId);
      if (data) {
        setHasCritical(data.some((i) => i.current_stock <= (i.min_threshold ?? 0)));
      }
    };
    checkCritical();
  }, [location.pathname, businessId]);

  const handleSaveBusinessName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) return;
    setSavingBusinessName(true);
    const name = businessNameDraft.trim() || null;
    const { error } = await supabase.from("businesses").upsert({ id: businessId, name }, { onConflict: "id" });
    setSavingBusinessName(false);
    setBusinessNameOpen(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      queryClient.invalidateQueries({ queryKey: ["business-name"] });
      toast({ title: "Business name saved", description: name ? `Display name set to "${name}".` : "Display name cleared." });
    }
  };

  const WHATSAPP_GROUP_URL = "https://wa.me/group"; // Replace with your group invite link

  const isAdmin = role === "admin" || isFounder;

  // Admin + Founder: full nav including Reports. Founder bypasses paywall.
  const adminGroups = [
    {
      label: "Overview",
      items: [
        { title: "BI Analyst", url: "/", icon: LayoutDashboard, desc: "7-day snapshot & AI insights" },
        { title: "Stock intelligence", url: "/stock-intelligence", icon: LineChart, desc: "Margin, reorder, dead stock" },
        { title: "Staff performance", url: "/staff-performance", icon: UserRound, desc: "Team sales & activity" },
        { title: "Inventory", url: "/inventory", icon: Package },
        { title: "Reports", url: "/reports", icon: BarChart3, desc: "Monthly P&L & official health score" },
      ],
    },
    {
      label: "Sales Management",
      items: [
        { title: "Manual Entry", url: "/manual-entry", icon: Pencil },
        { title: "Master Ledger", url: "/master-ledger", icon: BookOpen },
      ],
    },
    {
      label: "Operations",
      items: [
        { title: "Locations", url: "/locations", icon: MapPin },
        { title: "Expenses", url: "/expenses", icon: TrendingDown },
        { title: "Suppliers", url: "/suppliers", icon: Users },
        { title: "Transport & Logistics", url: "/transport-logistics", icon: Truck },
      ],
    },
    {
      label: "People",
      items: [{ title: "Team", url: "/team", icon: Users }],
    },
    { label: "System", items: [{ title: "System Logs", url: "/system-logs", icon: FileText }] },
    { label: "Account", items: [{ title: "Settings", url: "/settings", icon: Settings }] },
  ];

  const employeeRawNav = [
    { title: "Home", url: "/", icon: House, visible: true, proGated: false },
    { title: "Manual Entry", url: "/manual-entry", icon: Pencil, visible: employeeViewSettings.allowEmployeeRecordSales, proGated: false },
    { title: "My Activity", url: "/my-activity", icon: BookOpen, visible: employeeViewSettings.allowEmployeeViewSalesHistory, proGated: false },
    { title: "Expenses", url: "/expenses", icon: TrendingDown, visible: employeeViewSettings.allowEmployeeRecordExpenses, proGated: false },
    {
      title: "Transport & Logistics",
      url: "/transport-logistics",
      icon: Truck,
      visible: employeeViewSettings.allowEmployeeTransportLogistics,
      proGated: true,
    },
    { title: "Settings", url: "/settings", icon: Settings, visible: true, proGated: false },
  ];
  const employeeItems = employeeRawNav.filter((item) => item.visible);

  return (
    <Sidebar className="w-60 border-r-0">
      <div className="p-5 border-b border-sidebar-border">
        <h1 className="text-lg font-display font-bold text-sidebar-foreground">
          Review<span className="text-[#A7F0A7]" aria-hidden>Boost</span>
        </h1>
        <p className="text-[10px] uppercase tracking-[0.2em] text-sidebar-foreground/50 mt-1">
          Pro Manager
        </p>
        {(businessName || (isAdmin && businessId)) && (
          <div className="mt-2 flex items-center gap-1.5 min-w-0">
            {businessName ? (
              <span className="text-xs font-medium text-sidebar-foreground/80 truncate" title={businessName}>
                {businessName}
              </span>
            ) : isAdmin && businessId ? (
              <span className="text-xs text-sidebar-foreground/50">Set business name</span>
            ) : null}
            {isAdmin && businessId && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 text-sidebar-foreground/50 hover:text-sidebar-primary"
                onClick={() => {
                  setBusinessNameDraft(businessName ?? "");
                  setBusinessNameOpen(true);
                }}
                title={businessName ? "Edit business name" : "Set business name"}
              >
                <Building2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>

      <Dialog open={businessNameOpen} onOpenChange={setBusinessNameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Business name</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveBusinessName} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This name appears under &quot;ReviewBoost Pro Manager&quot; in the sidebar so your team sees your business.
            </p>
            <div className="space-y-2">
              <Label htmlFor="business-name">Display name</Label>
              <Input
                id="business-name"
                value={businessNameDraft}
                onChange={(e) => setBusinessNameDraft(e.target.value)}
                placeholder="e.g. Acme Store"
                maxLength={120}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setBusinessNameOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingBusinessName}>
                {savingBusinessName ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <SidebarContent>
        {isAdmin ? (
          adminGroups.map((group) => (
            <SidebarGroup key={group.label}>
              <SidebarGroupLabel className="text-sidebar-foreground/40 uppercase text-[10px] tracking-widest">
                {group.label}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {group.items.map((item) => {
                    const isTransportLogistics = item.url === "/transport-logistics";
                    const isGated = isTransportLogistics && !isPro;
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild>
                          {isGated ? (
                            <button
                              type="button"
                              onClick={() => { closeMobileSidebar(); openPaywall("transport_logistics"); }}
                              title="Upgrade to Pro for Transport & Logistics"
                              className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent transition-colors"
                            >
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </button>
                          ) : (
                            <NavLink
                              to={item.url}
                              end
                              onClick={closeMobileSidebar}
                              title={"desc" in item && item.desc ? `${item.title}: ${item.desc}` : item.title}
                              className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent transition-colors"
                              activeClassName="bg-sidebar-accent text-sidebar-primary font-semibold"
                            >
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                              {item.title === "Inventory" && hasCritical && (
                                <AlertTriangle className="h-3.5 w-3.5 ml-auto text-critical" />
                              )}
                            </NavLink>
                          )}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))
        ) : (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/40 uppercase text-[10px] tracking-widest">
              Navigation
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {employeeItems.map((item) => {
                  const gated = item.proGated && !isPro;
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        {gated ? (
                          <button
                            type="button"
                            onClick={() => {
                              closeMobileSidebar();
                              openPaywall("transport_logistics");
                            }}
                            title="Upgrade to Pro for Transport & Logistics"
                            className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent transition-colors"
                          >
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </button>
                        ) : (
                          <NavLink
                            to={item.url}
                            end
                            onClick={closeMobileSidebar}
                            className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent transition-colors"
                            activeClassName="bg-sidebar-accent text-sidebar-primary font-semibold"
                          >
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </NavLink>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {isAdmin && !isFounder && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/40 uppercase text-[10px] tracking-widest flex items-center gap-2">
              Subscription
              <Badge variant={isPro ? "default" : "secondary"} className="text-[9px] px-1.5 py-0">
                {isPro ? "Pro" : "Free"}
              </Badge>
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <div className="px-3 py-2 rounded-md bg-sidebar-accent/50 space-y-2">
                {profile?.subscription_valid_until ? (
                  <>
                    <div className="flex items-center gap-2 text-xs">
                      <Calendar className="h-3.5 w-3.5 text-sidebar-foreground/70" />
                      <span className="text-sidebar-foreground/80">
                        Valid until{" "}
                        {new Date(profile.subscription_valid_until).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                    <p className="text-[10px] text-sidebar-foreground/50">Renew before this date to stay active</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full h-8 text-xs"
                      onClick={() => openPaywall("upgrade_cta")}
                    >
                      <CreditCard className="h-3.5 w-3.5 mr-1.5" />
                      Renew for next month
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-sidebar-foreground/70">
                      {profile?.subscription_status === "active" ? "Active (legacy)" : "No active plan"}
                    </p>
                    <Button
                      size="sm"
                      className="w-full h-8 text-xs"
                      onClick={() => openPaywall("upgrade_cta")}
                    >
                      <CreditCard className="h-3.5 w-3.5 mr-1.5" />
                      Subscribe
                    </Button>
                  </>
                )}
                <NavLink
                  to="/pricing"
                  onClick={closeMobileSidebar}
                  className="block text-center text-[11px] text-sidebar-foreground/70 hover:text-sidebar-foreground mt-2"
                >
                  View plans & pricing
                </NavLink>
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/40 uppercase text-[10px] tracking-widest">
            Team
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <button
              type="button"
              onClick={() => window.open(WHATSAPP_GROUP_URL, "_blank", "noopener,noreferrer")}
              className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent transition-colors text-left"
            >
              <MessageCircle className="h-4 w-4" />
              <span>WhatsApp Group</span>
            </button>
          </SidebarGroupContent>
        </SidebarGroup>
        {profile?.role === "founder" && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/40 uppercase text-[10px] tracking-widest">
              Founder
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to="/admin"
                      end
                      onClick={closeMobileSidebar}
                      title="Founder dashboard: MRR, users, VIP, grace"
                      className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent transition-colors"
                      activeClassName="bg-sidebar-accent text-sidebar-primary font-semibold"
                    >
                      <Shield className="h-4 w-4" />
                      <span>Admin</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-4">
        <div className="flex items-center gap-3 mb-2">
          {profileLoading ? (
            <div className="h-8 w-8 shrink-0 rounded-full bg-sidebar-accent animate-pulse" aria-hidden />
          ) : (
            <UserAvatar size="sm" userId={user?.id} avatarUrl={profile?.avatar_url} fullName={profile?.full_name} />
          )}
          <div className="flex flex-col min-w-0 flex-1">
            <div className="text-xs text-sidebar-foreground/50 truncate">
              {user?.email}
            </div>
            {(role === "admin" || isFounder) && (
              <span className="inline-flex items-center gap-1.5 mt-0.5">
                <span className="inline-flex rounded-full border border-sidebar-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sidebar-primary">
                  {isFounder ? "Founder" : "Admin"}
                </span>
                {isAdmin && !isFounder && (
                  <Badge variant={isPro ? "default" : "secondary"} className="text-[9px] px-1.5 py-0">
                    {isPro ? "Pro" : "Free"}
                  </Badge>
                )}
                {isFounder && (
                  <Badge variant="default" className="text-[9px] px-1.5 py-0">Pro</Badge>
                )}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => refreshProfile()}
          className="flex items-center gap-2 text-sm text-sidebar-foreground/60 hover:text-sidebar-primary transition-colors w-full mb-1"
        >
          <RefreshCw className="h-4 w-4" />
          {isFounder ? "Retry Profile Sync" : "Refresh my account"}
        </button>
        <button
          onClick={signOut}
          className="flex items-center gap-2 text-sm text-sidebar-foreground/60 hover:text-critical transition-colors w-full"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </button>
      </SidebarFooter>
    </Sidebar>
  );
}
